import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Form, Button, Card, Alert } from 'react-bootstrap';
import { 
  Phone, 
  Mail, 
  MapPin, 
  Send,
  MessageCircle,
  Clock,
  Facebook,
  Instagram,
  Twitter,
  MessageSquare,
  ChevronDown,
  CheckCircle
} from 'lucide-react';
import "../../../assets/css/general/Contact.css";
import NavigationBar from '../../../components/user/general/NavigationBar';
import Footer from '../footer/Footer';

const Contact = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    subject: '',
    category: '',
    message: ''
  });

  const [hoveredSocial, setHoveredSocial] = useState(null);
  const [expandedFAQ, setExpandedFAQ] = useState(null);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formErrors, setFormErrors] = useState({});

  // Smooth scroll to top on component mount
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    
    // Clear error for this field when user starts typing
    if (formErrors[name]) {
      setFormErrors({ ...formErrors, [name]: '' });
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.fullName.trim()) {
      errors.fullName = 'Name is required';
    }
    
    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Email is invalid';
    }
    
    if (!formData.subject.trim()) {
      errors.subject = 'Subject is required';
    }
    
    if (!formData.category) {
      errors.category = 'Please select a category';
    }
    
    if (!formData.message.trim()) {
      errors.message = 'Message is required';
    } else if (formData.message.trim().length < 10) {
      errors.message = 'Message must be at least 10 characters';
    }
    
    return errors;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const errors = validateForm();
    
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    
    setIsSubmitting(true);
    
    // Simulate API call
    setTimeout(() => {
      setSubmitStatus('success');
      setIsSubmitting(false);
      
      // Reset form
      setFormData({
        fullName: '',
        email: '',
        subject: '',
        category: '',
        message: ''
      });
      
      // Clear success message after 5 seconds
      setTimeout(() => setSubmitStatus(null), 5000);
    }, 1500);
  };

  const toggleFAQ = (index) => {
    setExpandedFAQ(expandedFAQ === index ? null : index);
  };

  // Contact info with updated colors
  const contactInfo = [
    { 
      icon: Phone, 
      title: "Phone Number", 
      content: "+92 300 1234567", 
      color: "#165C5C", 
      link: "tel:+923001234567" 
    },
    { 
      icon: Mail, 
      title: "Email Address", 
      content: "support@smartdonation.com", 
      color: "#FFB800", 
      link: "mailto:support@smartdonation.com" 
    },
    { 
      icon: MapPin, 
      title: "Office Address", 
      content: "Gujranwala, Punjab, Pakistan", 
      color: "#165C5C", 
      link: "#" 
    },
    { 
      icon: Clock, 
      title: "Working Hours", 
      content: "Mon - Fri: 9:00 AM - 6:00 PM", 
      color: "#FFB800", 
      link: "#" 
    }
  ];

  const socialMedia = [
    { icon: Facebook, name: "Facebook", link: "https://facebook.com", color: "#1877F2" },
    { icon: Instagram, name: "Instagram", link: "https://instagram.com", color: "#E4405F" },
    { icon: Twitter, name: "Twitter", link: "https://twitter.com", color: "#1DA1F2" },
    { icon: MessageSquare, name: "WhatsApp", link: "https://wa.me/923001234567", color: "#25D366" }
  ];

  const categories = [
    "General Help", 
    "Donation Issue", 
    "Account Support", 
    "Payment / Wallet Issue", 
    "NGO Verification", 
    "Complaint"
  ];

  const faqs = [
    { 
      question: "How to donate?", 
      answer: "Register on our platform → Browse available donations → Choose a cause that resonates with you → Submit your donation details securely → Receive confirmation and track your contribution." 
    },
    { 
      question: "How to request help?", 
      answer: "Create a receiver account → Complete your profile verification → Post your request with detailed information → Provide necessary documentation → Wait for approval and connect with donors." 
    },
    { 
      question: "How long does verification take?", 
      answer: "NGO verification typically takes 24-48 hours after document submission. Our team carefully reviews all credentials to ensure authenticity and transparency for donors." 
    },
    { 
      question: "Is my donation tax-deductible?", 
      answer: "Yes, donations made through registered NGOs are tax-deductible. You will receive a receipt that can be used for tax purposes. Please consult with your tax advisor for specific guidance." 
    },
    { 
      question: "How can I track my donation?", 
      answer: "Once your donation is processed, you can track it through your dashboard. You'll receive real-time updates on how your contribution is being used and the impact it's making." 
    }
  ];

  return (
    <div className="contact-page">
         <NavigationBar/>
      {/* Hero Section */}
      <section className="contact-hero">
        <div className="contact-hero-overlay"></div>
        <Container className="text-center">
          <Row className="justify-content-center">
            <Col lg={8}>
              <MessageCircle size={80} className="contact-hero-icon" />
              <h1 className="contact-hero-title">Get in Touch With Us</h1>
              <p className="contact-hero-subtitle">
                If you have any questions, problems, or need help related to donations, 
                NGOs or accounts, feel free to contact us. We are here to help you 24/7.
              </p>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Main Contact Section */}
      <section className="main-contact-section">
        <Container>
          <Row className="g-4">

            {/* Contact Form */}
            <Col lg={8}>
              <Card className="contact-form-card">
                <Card.Body>
                  <div className="form-header">
                    <Send size={40} className="form-icon" />
                    <h2 className="form-title">Send Us a Message</h2>
                    <p className="form-subtitle">
                      Fill out the form below and we'll get back to you within 24 hours
                    </p>
                  </div>

                  {submitStatus === 'success' && (
                    <Alert variant="success" className="success-alert">
                      <CheckCircle size={20} /> 
                      Message sent successfully! We'll get back to you soon.
                    </Alert>
                  )}

                  <Form onSubmit={handleSubmit}>
                    <Row className="g-3">
                      <Col md={6}>
                        <Form.Control
                          type="text"
                          name="fullName"
                          placeholder="Full Name *"
                          value={formData.fullName}
                          onChange={handleChange}
                          isInvalid={!!formErrors.fullName}
                          disabled={isSubmitting}
                        />
                        <Form.Control.Feedback type="invalid">
                          {formErrors.fullName}
                        </Form.Control.Feedback>
                      </Col>
                      
                      <Col md={6}>
                        <Form.Control
                          type="email"
                          name="email"
                          placeholder="Email Address *"
                          value={formData.email}
                          onChange={handleChange}
                          isInvalid={!!formErrors.email}
                          disabled={isSubmitting}
                        />
                        <Form.Control.Feedback type="invalid">
                          {formErrors.email}
                        </Form.Control.Feedback>
                      </Col>
                      
                      <Col md={6}>
                        <Form.Control
                          type="text"
                          name="subject"
                          placeholder="Subject *"
                          value={formData.subject}
                          onChange={handleChange}
                          isInvalid={!!formErrors.subject}
                          disabled={isSubmitting}
                        />
                        <Form.Control.Feedback type="invalid">
                          {formErrors.subject}
                        </Form.Control.Feedback>
                      </Col>
                      
                      <Col md={6}>
                        <Form.Select
                          name="category"
                          value={formData.category}
                          onChange={handleChange}
                          isInvalid={!!formErrors.category}
                          disabled={isSubmitting}
                        >
                          <option value="">Select Category *</option>
                          {categories.map((c, i) => (
                            <option key={i} value={c}>{c}</option>
                          ))}
                        </Form.Select>
                        <Form.Control.Feedback type="invalid">
                          {formErrors.category}
                        </Form.Control.Feedback>
                      </Col>
                      
                      <Col md={12}>
                        <Form.Control
                          as="textarea"
                          rows={5}
                          name="message"
                          placeholder="Your Message (minimum 10 characters) *"
                          value={formData.message}
                          onChange={handleChange}
                          isInvalid={!!formErrors.message}
                          disabled={isSubmitting}
                        />
                        <Form.Control.Feedback type="invalid">
                          {formErrors.message}
                        </Form.Control.Feedback>
                      </Col>
                      
                      <Col md={12}>
                        <Button type="submit" disabled={isSubmitting}>
                          <Send size={18} /> 
                          {isSubmitting ? 'Sending...' : 'Send Message'}
                        </Button>
                      </Col>
                    </Row>
                  </Form>
                </Card.Body>
              </Card>
            </Col>

            {/* Contact Info + Social Media */}
            <Col lg={4}>
              {contactInfo.map((info, i) => {
                const Icon = info.icon;
                return (
                  <Card key={i} className="mb-3">
                    <Card.Body className="info-card-body">
                      <div 
                        className="info-icon-wrapper"
                        style={{ 
                          backgroundColor: `${info.color}20`, 
                          color: info.color 
                        }}
                      >
                        <Icon size={24} />
                      </div>
                      <h5>{info.title}</h5>
                      <p>
                        {info.link !== "#" ? (
                          <a 
                            href={info.link} 
                            style={{ 
                              color: 'inherit', 
                              textDecoration: 'none' 
                            }}
                          >
                            {info.content}
                          </a>
                        ) : (
                          info.content
                        )}
                      </p>
                    </Card.Body>
                  </Card>
                );
              })}

              <Card className="social-card">
                <Card.Body>
                  <h5>Follow Us</h5>
                  <div className="social-icons">
                    {socialMedia.map((social, i) => {
                      const Icon = social.icon;
                      const isHovered = hoveredSocial === i;
                      return (
                        <a
                          key={i}
                          href={social.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          onMouseEnter={() => setHoveredSocial(i)}
                          onMouseLeave={() => setHoveredSocial(null)}
                          aria-label={social.name}
                          style={{
                            background: isHovered ? social.color : '#f8f9fa',
                            color: isHovered ? 'white' : '#666',
                            display: 'inline-flex',
                            padding: '10px',
                            borderRadius: '50%',
                            marginRight: '8px',
                            transition: 'all 0.3s ease',
                            cursor: 'pointer'
                          }}
                        >
                          <Icon size={20} />
                        </a>
                      );
                    })}
                  </div>
                </Card.Body>
              </Card>
            </Col>

          </Row>
        </Container>
      </section>

      {/* Map Section */}
      <section className="map-section">
        <Container>
          <Card className="map-card">
            <Card.Body>
              <h2>Our Location</h2>
              <div className="map-wrapper">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d13594.739326779594!2d74.18375!3d32.15683!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x391f29cb5f138d1b%3A0x9c23e563f5d5f07e!2sGujranwala%2C%20Punjab%2C%20Pakistan!5e0!3m2!1sen!2s!4v1234567890"
                  width="100%"
                  height="400"
                  style={{ border: 0, borderRadius: '15px' }}
                  allowFullScreen=""
                  loading="lazy"
                  title="Office Location"
                />
              </div>
            </Card.Body>
          </Card>
        </Container>
      </section>

      {/* FAQ Section */}
      <section className="faq-section">
        <Container>
          <Row className="justify-content-center">
            <Col lg={10}>
              <h2>Frequently Asked Questions</h2>
              {faqs.map((faq, i) => (
                <Card 
                  key={i} 
                  className={`faq-card ${expandedFAQ === i ? 'expanded' : ''}`}
                >
                  <Card.Body>
                    <div 
                      onClick={() => toggleFAQ(i)} 
                      style={{ 
                        cursor: 'pointer', 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        userSelect: 'none'
                      }}
                    >
                      <h5>{faq.question}</h5>
                      <ChevronDown 
                        className={`${expandedFAQ === i ? 'rotated' : ''}`}
                        size={24}
                        style={{ flexShrink: 0, marginLeft: '15px' }}
                      />
                    </div>
                    {expandedFAQ === i && (
                      <p style={{ 
                        animation: 'slideDown 0.3s ease',
                        marginTop: '15px',
                        paddingTop: '15px',
                        borderTop: '2px solid #f0f0f0'
                      }}>
                        {faq.answer}
                      </p>
                    )}
                  </Card.Body>
                </Card>
              ))}
            </Col>
          </Row>
        </Container>
      </section>
        <Footer/>
    </div>
  );
};

export default Contact;